import React, { Component } from 'react';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { X, Upload, Download, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

// Adjust this path if your template lives elsewhere in /public
const TEMPLATE_URL = '/templates/Agent_Bulk_Upload_Template.xlsx';

class BulkAgentUpload extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stage:      'upload',   // upload -> previewing -> importing -> done
      fileName:   '',
      rows:       [],         // parsed + validated rows from server
      summary:    null,       // { total, valid, invalid, duplicate }
      importing:  false,
      result:     null,
    };
    this.fileInputRef = React.createRef();
  }

  handlePickFile() {
    this.fileInputRef.current?.click();
  }

  async handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    this.setState({ fileName: file.name, stage: 'previewing', rows: [], summary: null });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/agents/bulk-upload/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      this.setState({ rows: data.rows || [], summary: data.summary || null });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to read the file. Please check the format and try again.');
      this.setState({ stage: 'upload' });
    } finally {
      if (this.fileInputRef.current) this.fileInputRef.current.value = '';
    }
  }

  async handleConfirmImport() {
    const validRows = this.state.rows.filter(r => r.status === 'valid');
    if (validRows.length === 0) {
      toast.error('No valid rows to import.');
      return;
    }
    this.setState({ importing: true });
    try {
      const { data } = await api.post('/agents/bulk-upload/confirm', {
        rows: validRows.map(r => r.data),
      });
      this.setState({ stage: 'done', result: data, importing: false });
      toast.success(data.message || `${data.inserted || validRows.length} agents imported successfully!`);
      if (this.props.onImported) this.props.onImported();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed. Please try again.');
      this.setState({ importing: false });
    }
  }

  handleReset() {
    this.setState({ stage: 'upload', fileName: '', rows: [], summary: null, result: null });
  }

  render() {
    const { onClose } = this.props;
    const { stage, fileName, rows, summary, importing, result } = this.state;

    const validRows   = rows.filter(r => r.status === 'valid');
    const invalidRows = rows.filter(r => r.status === 'invalid');
    const dupRows     = rows.filter(r => r.status === 'duplicate');

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
        <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 880, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', background: '#16213E', color: '#fff' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Bulk Agent Upload</div>
              <div style={{ fontSize: 12, color: '#93C5FD', marginTop: 2 }}>Import multiple agents at once from an Excel file</div>
            </div>
            <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>

            {/* ── STAGE: upload ── */}
            {stage === 'upload' && (
              <div>
                <a href={TEMPLATE_URL} download
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#16213E', background: '#EEF2FF', border: '1.5px solid #C7D2FE', borderRadius: 8, padding: '8px 14px', textDecoration: 'none', marginBottom: 18 }}>
                  <Download size={14} /> Download Excel Template
                </a>

                <div
                  onClick={this.handlePickFile.bind(this)}
                  style={{ border: '2px dashed #C7D2FE', borderRadius: 12, padding: '48px 20px', textAlign: 'center', cursor: 'pointer', background: '#FAFBFF' }}>
                  <Upload size={30} color="#6366F1" style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#16213E' }}>Click to select an Excel file</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>.xlsx or .xls — use the template above for the correct columns</div>
                  <input ref={this.fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={this.handleFileChange.bind(this)} />
                </div>

                <div style={{ marginTop: 18, fontSize: 12, color: '#6B7280', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 14px' }}>
                  Required columns: <strong>Agency Name, Director Name, Phone, Operation Type, Agent Type</strong>.
                  Optional: Email, Credit Limit, Status. See the "Instructions" sheet in the template for exact accepted values.
                </div>
              </div>
            )}

            {/* ── STAGE: previewing (parsing in progress, no rows yet) ── */}
            {stage === 'previewing' && rows.length === 0 && !summary && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', color: '#6B7280' }}>
                <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} />
                Reading {fileName}…
              </div>
            )}

            {/* ── STAGE: previewing (rows loaded) ── */}
            {stage === 'previewing' && rows.length > 0 && (
              <div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, background: '#EEF2FF', color: '#3730A3', borderRadius: 20, padding: '4px 12px' }}>Total: {rows.length}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, background: '#D7F5E6', color: '#0B7A4C', borderRadius: 20, padding: '4px 12px' }}>✓ Valid: {validRows.length}</span>
                  {dupRows.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, background: '#FEF3C7', color: '#92400E', borderRadius: 20, padding: '4px 12px' }}>⚠ Duplicate: {dupRows.length}</span>}
                  {invalidRows.length > 0 && <span style={{ fontSize: 12, fontWeight: 700, background: '#FCEAE8', color: '#B5382D', borderRadius: 20, padding: '4px 12px' }}>✗ Invalid: {invalidRows.length}</span>}
                </div>

                <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#F3F4F6', zIndex: 1 }}>
                        <tr>
                          <th style={thStyle}>#</th>
                          <th style={thStyle}>Agency Name</th>
                          <th style={thStyle}>Director</th>
                          <th style={thStyle}>Phone</th>
                          <th style={thStyle}>Operation Type</th>
                          <th style={thStyle}>Agent Type</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Issue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={i} style={{ background: r.status === 'invalid' ? '#FCEAE8' : r.status === 'duplicate' ? '#FFFBEB' : i % 2 ? '#FAFBFF' : '#fff' }}>
                            <td style={tdStyle}>{i + 1}</td>
                            <td style={tdStyle}>{r.data.agency_name}</td>
                            <td style={tdStyle}>{r.data.director_name}</td>
                            <td style={tdStyle}>{r.data.phone}</td>
                            <td style={tdStyle}>{r.data.operation_type}</td>
                            <td style={tdStyle}>{r.data.agent_type}</td>
                            <td style={tdStyle}>
                              {r.status === 'valid' && <span style={{ color: '#0FA968', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={13} /> Ready</span>}
                              {r.status === 'duplicate' && <span style={{ color: '#B45309' }}>Duplicate</span>}
                              {r.status === 'invalid' && <span style={{ color: '#E1483A' }}>Invalid</span>}
                            </td>
                            <td style={{ ...tdStyle, color: '#B5382D', fontSize: 11 }}>{(r.errors || []).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {invalidRows.length > 0 && (
                  <div style={{ marginTop: 12, fontSize: 12, color: '#B5382D', background: '#FCEAE8', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
                    Invalid and duplicate rows will be skipped automatically. Only the {validRows.length} valid row{validRows.length !== 1 ? 's' : ''} will be imported.
                  </div>
                )}
              </div>
            )}

            {/* ── STAGE: done ── */}
            {stage === 'done' && result && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <CheckCircle2 size={40} color="#0FA968" style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 16, fontWeight: 700, color: '#16213E' }}>Import Complete</div>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 6 }}>
                  {result.inserted || 0} agent{(result.inserted || 0) !== 1 ? 's' : ''} added successfully.
                  {result.skipped > 0 && ` ${result.skipped} row${result.skipped !== 1 ? 's' : ''} skipped.`}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 22px', borderTop: '1px solid #E5E7EB', background: '#FAFAFA' }}>
            <div>
              {stage === 'previewing' && (
                <button type="button" onClick={this.handleReset.bind(this)} style={{ fontSize: 13, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                  ← Choose a different file
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ height: 38, padding: '0 18px', borderRadius: 8, border: '1.5px solid #D1D5DB', background: '#fff', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                {stage === 'done' ? 'Close' : 'Cancel'}
              </button>
              {stage === 'previewing' && (
                <button type="button" onClick={this.handleConfirmImport.bind(this)} disabled={importing || validRows.length === 0}
                  style={{ height: 38, padding: '0 20px', borderRadius: 8, border: 'none', background: importing || validRows.length === 0 ? '#9CA3AF' : '#16213E', color: '#fff', fontSize: 13, fontWeight: 700, cursor: importing || validRows.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {importing ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
                  {importing ? 'Importing…' : `Import ${validRows.length} Agent${validRows.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }
}

const thStyle = { textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.05em', borderBottom: '1px solid #E5E7EB' };
const tdStyle = { padding: '7px 10px', borderBottom: '1px solid #F1F5F9', color: '#374151' };

export default BulkAgentUpload;